import { Box, Typography } from "@mui/material";
import GooglePlacesAutocomplete, {
    geocodeByPlaceId,
} from "react-google-places-autocomplete";
import { useLoadScript } from "@react-google-maps/api";

const libraries: ("places" | "geometry")[] = ["places"];

export interface ILocationResponsePayload {
  country: string;
  state: string;
  city: string;
  district: string;
  postalCode: string;
  address: string;
  area:string;
}

interface ILocationAutoCompleteProps {
  onLocationChange: (data: ILocationResponsePayload) => void;
  id?: string | number;
  value: string | number | null;
  [key: string]: any;
  error?: boolean;
  helperText?: string;
}

export const LocationAutoComplete = (props: ILocationAutoCompleteProps) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
    libraries,
  });

  return (
    <Box>
      {isLoaded ? (
        <GooglePlacesAutocomplete
          key={props.value}
          selectProps={{
            id: props.id as string,
            placeholder: "Choose Location",
            isClearable: true,
            defaultInputValue: props.value as string,
            onChange: async (place: any) => {
              const placeId = place?.value.place_id;

              if (!placeId) return;

              try {
                // Get the detailed place information using the placeId
                const response = await geocodeByPlaceId(placeId);
                console.log("response ===> ", response);
                const { address_components } = response[0];

                let city = "";
                let state = "";
                let country = "";
                let address = "";
                let zipCode = "";
                let district = "";
                let area = "";
                let locality = "";
                let sublocalities: string[] = [];

                // Extract city, state, country, zip code, and address from the address_components
                for (const component of address_components) {
                  const types = component.types;
                  if (types.includes("locality")) {
                    locality = component.long_name;
                  } else if (types.includes("administrative_area_level_1")) {
                    state = component.long_name;
                  } else if (types.includes("administrative_area_level_2")) {
                    district = component.long_name;
                  } else if (types.includes("administrative_area_level_3")) {
                    // Some regions use level 3 for district/tehsil
                    if (!district) district = component.long_name;
                  } else if (types.includes("country")) {
                    country = component.long_name;
                  } else if (types.includes("postal_code")) {
                    zipCode = component.long_name;
                  } else if (
                    types.includes("sublocality") ||
                    types.includes("sublocality_level_1") ||
                    types.includes("sublocality_level_2") ||
                    types.includes("neighborhood")
                  ) {
                    sublocalities.push(component.long_name);
                  }

                  // Address construction (street info)
                  if (
                    types.includes("street_number") ||
                    types.includes("route") ||
                    types.includes("premise")
                  ) {
                    address += component.long_name + " ";
                  }
                }

                const isDivision = (s: string) => /division/i.test(s);

                // RSA logic for City and Area:
                // 1. If we have sublocalities (e.g., "Sector 18"), they are the Area, and Town/Locality is the City.
                // 2. If we don't have sublocalities (short address), we try to use District as City and Town/Locality as Area.
                // 3. We avoid names containing "Division" for City if possible.

                if (sublocalities.length > 0) {
                  area = sublocalities.join(", ");
                  city = locality || district || "";
                  // If city is a division, try to use locality instead
                  if (isDivision(city) && locality && !isDivision(locality)) {
                    city = locality;
                  }
                } else {
                  // Short address fallback
                  area = locality || "";
                  city = locality || "";

                  if (district && !isDivision(district)) {
                    if (locality && locality !== district) {
                      city = district;
                      area = locality;
                    } else {
                      city = district;
                      area = district; // Fallback to district if locality is missing
                    }
                  }
                }

                props.onLocationChange({
                  country: country,
                  address: address || place.label,
                  city: city,
                  district:district,
                  state: state,
                  postalCode: zipCode,
                  area:area,
                });
              } catch (error) {
                console.error("Error fetching geocode data:", error);
              }
            },
          }}
          autocompletionRequest={{
            componentRestrictions: {
              country: ["in"],
            },
          }}
          {...props}
        />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
          Loading Location Service...
        </Typography>
      )}
      <Typography
        variant="subtitle2"
        sx={{
          fontSize: "0.75rem",
          color: "#e46a76",
          marginLeft: 2,
          marginTop: 0.75,
        }}
      >
        {props.error && props.helperText}
      </Typography>
    </Box>
  );
};

interface IAddressAutoCompleteProps {
  onLocationChange: (data: string) => void;
  id?: string | number;
  value: string | number | null;
  [key: string]: any;
}

export const AddressAutoComplete = (props: IAddressAutoCompleteProps) => {
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY || "",
    libraries,
  });

  if (!isLoaded) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        Loading Location Service...
      </Typography>
    );
  }

  return (
    <GooglePlacesAutocomplete
      key={props.value}
      selectProps={{
        id: props.id as string,
        styles: {
          control: (provided: any) => ({
            ...provided,
            padding: "6px",
            maxHeight: "40px",
          }),
        },
        placeholder: "Choose Location",
        isClearable: true,
        defaultInputValue: props.value as string,
        onChange: (value: any) => props.onLocationChange(value),
      }}
      autocompletionRequest={{
        componentRestrictions: {
          country: ["in"],
        },
      }}
      {...props}
    />
  );
};
